/**
 * useEmployeeScheduleViewState.ts
 * View-mode + date-window state for the Employee Schedule (UX overhaul).
 *
 * View mode (matrix | board | timeline) is the source of truth in the URL
 * (?view=) mirrored to localStorage, so a refresh / shared link restores it.
 * The anchor week + week count drive the data window; the Weekly Board renders
 * just the anchor week, Matrix/Timeline render the full window.
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { currentSaturday, addWeeksISO, fridayAfter } from '../../../components/schedule/employeeScheduleShared';

export type EmployeeViewMode = 'matrix' | 'board' | 'timeline';
const LS_KEY = 'belab.employeeSchedule.view';
const DEFAULT_WEEK_COUNT = 8;
const VALID: EmployeeViewMode[] = ['matrix', 'board', 'timeline'];

function initialView(urlView: string | null): EmployeeViewMode {
  if (urlView && VALID.includes(urlView as EmployeeViewMode)) return urlView as EmployeeViewMode;
  try {
    const ls = localStorage.getItem(LS_KEY);
    if (ls && VALID.includes(ls as EmployeeViewMode)) return ls as EmployeeViewMode;
  } catch { /* ignore */ }
  return 'matrix';
}

export interface EmployeeScheduleViewState {
  viewMode: EmployeeViewMode;
  setViewMode: (m: EmployeeViewMode) => void;
  anchorWeek: string;
  setAnchorWeek: (sat: string) => void;
  weekCount: number;
  windowStart: string;
  windowEnd: string;
  goToday: () => void;
  goPrev: () => void;
  goNext: () => void;
  /** Grow the window by one week at the END (matrix ▶ arrow loads more weeks). */
  extendWeeks: () => void;
}

export function useEmployeeScheduleViewState(initialAnchor?: string): EmployeeScheduleViewState {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewModeState] = useState<EmployeeViewMode>(() => initialView(searchParams.get('view')));
  const [anchorWeek, setAnchorWeek] = useState<string>(() => initialAnchor || currentSaturday());
  // Grows one week at a time via the matrix ▶ edge arrow (2026-07-26 — the
  // arrow LOADS weeks, it doesn't just reveal them). Capped well past any real
  // planning horizon; "Today" resets to the default window.
  const [weekCount, setWeekCount] = useState(DEFAULT_WEEK_COUNT);
  const extendWeeks = useCallback(() => setWeekCount((c) => Math.min(c + 1, 52)), []);

  const setViewMode = useCallback((m: EmployeeViewMode) => {
    setViewModeState(m);
    try { localStorage.setItem(LS_KEY, m); } catch { /* ignore */ }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', m);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const goToday = useCallback(() => { setAnchorWeek(currentSaturday()); setWeekCount(DEFAULT_WEEK_COUNT); }, []);
  const goPrev = useCallback(() => setAnchorWeek((w) => addWeeksISO(w, -1)), []);
  const goNext = useCallback(() => setAnchorWeek((w) => addWeeksISO(w, 1)), []);

  // Board needs only the anchor week; Matrix/Timeline need the full window.
  const { windowStart, windowEnd } = useMemo(() => {
    if (viewMode === 'board') {
      return { windowStart: anchorWeek, windowEnd: fridayAfter(anchorWeek, 1) };
    }
    return { windowStart: anchorWeek, windowEnd: fridayAfter(anchorWeek, weekCount) };
  }, [viewMode, anchorWeek, weekCount]);

  return {
    viewMode, setViewMode, anchorWeek, setAnchorWeek, weekCount,
    windowStart, windowEnd, goToday, goPrev, goNext, extendWeeks,
  };
}
